import type { ReferrerInstall } from "@redotech/admin-sdk/rpc/schema/referrers/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import ExternalLinkIcon from "@redotech/redo-icons/link-external-02.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoTable,
  RedoTableEmptyStateContent,
  RedoTableLoadingStateContent,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import { RedoTablePagination } from "@redotech/redo-web/arbiter-components/tables/redo-table-pagination";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";

import { RedoAdminRpcClientContext } from "../../app/redo-admin-rpc-client-provider";
import * as listStyles from "../../onboardings/returns/returns-page.module.css";
import { OrderVolumeTooltipCell } from "../../team/order-volume-tooltip-cell";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

interface ReferrerInstallsListProps {
  installCount: number;
  referrerId: string;
}

export const ReferrerInstallsList = memo(function ReferrerInstallsList({
  installCount,
  referrerId,
}: ReferrerInstallsListProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const [pageSize, setPageSizeState] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const offset = (currentPage - 1) * pageSize;

  const installsLoad = useLoad(
    async (signal) =>
      rpcClient.listReferrerInstalls(
        { referrerId, offset, limit: pageSize },
        { signal },
      ),
    [currentPage, offset, pageSize, referrerId, refreshKey, rpcClient],
  );
  const installs = installsLoad.value?.installs ?? [];
  const total = installsLoad.value?.total ?? installCount;
  const loadingInitial = installsLoad.pending && installsLoad.value == null;
  const columns = useMemo(
    () => INSTALLS_COLUMNS as RedoTableColumn<ReferrerInstall>[],
    [],
  );

  const retry = useCallback(() => setRefreshKey((key) => key + 1), []);
  const setPageSize = useCallback((nextPageSize: number) => {
    setPageSizeState(nextPageSize);
    setCurrentPage(1);
  }, []);

  return (
    <div className={listStyles.allMerchantsContent}>
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold">
          {total.toLocaleString()} attributed install{total === 1 ? "" : "s"}
        </Text>
      </Flex>
      <div className={listStyles.tableContainerWithSidebar}>
        <div className={listStyles.tableCard}>
          {installsLoad.error && installsLoad.value == null ? (
            <div className={listStyles.tableErrorState}>
              Failed to load installs.{" "}
              <RedoButton
                hierarchy="link"
                onClick={retry}
                size="xs"
                text="Retry"
              />
            </div>
          ) : (
            <>
              <RedoTable<ReferrerInstall>
                emptyStateContent={
                  <RedoTableEmptyStateContent text="No attributed installs yet." />
                }
                layout={{ columns }}
                loadingStateContent={
                  loadingInitial ? <RedoTableLoadingStateContent /> : undefined
                }
                rowsOfData={installs}
              />
              <RedoTablePagination
                currentPage={currentPage}
                knownRowCount={total}
                loading={installsLoad.pending}
                pageSize={pageSize}
                pageSizeLabel="installs per page"
                pageSizes={PAGE_SIZE_OPTIONS}
                setCurrentPage={setCurrentPage}
                setPageSize={setPageSize}
                showWhenSinglePage={total > 0}
                size="sm"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
});

const INSTALLS_COLUMNS: RedoTableColumn<ReferrerInstall>[] = [
  {
    key: "shopName",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Merchant name" />
    ),
    renderNormalCell: ({ row }) => <MerchantNameCell install={row} />,
  },
  {
    key: "shopDomain",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Store domain" />
    ),
    renderNormalCell: ({ row }) => <RedoTableTextCell text={row.shopDomain} />,
  },
  {
    key: "productCode",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Product" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={productCodeLabel(row.productCode)} />
    ),
  },
  {
    key: "onlineStoreOrderVolume",
    widthMode: { type: "hug" },
    alignment: "left",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Online orders (1y)" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTablePaddedCell>
        <OrderVolumeTooltipCell snapshot={row.metricsSnapshot} />
      </RedoTablePaddedCell>
    ),
  },
  {
    key: "installedAt",
    renderHeaderCell: () => (
      <StandardRedoTableHeaderCell headerText="Installed" />
    ),
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={new Date(row.installedAt).toLocaleString()} />
    ),
  },
];

const MerchantNameCell = memo(function MerchantNameCell({
  install,
}: {
  install: ReferrerInstall;
}) {
  if (!install.teamId) {
    return <RedoTableTextCell leading text={install.shopName} />;
  }
  const merchantAdminPath = getMerchantAdminPath(install.teamId);

  return (
    <RedoTablePaddedCell>
      <Flex align="center" gap="xs" minW="none">
        <Flex
          as="a"
          linkProps={{
            rel: "noopener noreferrer",
            target: "_blank",
            to: merchantAdminPath,
          }}
          minW="none"
          onClick={(event) => event.stopPropagation()}
          textDecoration="none"
        >
          <Text
            fontSize="xs"
            fontWeight="medium"
            textColor="primary"
            textDecoration="none"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {install.shopName}
          </Text>
        </Flex>
        <Flex
          align="center"
          aria-label={`Open ${install.shopName} in admin`}
          as="a"
          hoverTextColor="primary"
          justify="center"
          linkProps={{
            rel: "noopener noreferrer",
            target: "_blank",
            to: merchantAdminPath,
          }}
          onClick={(event) => event.stopPropagation()}
          textColor="secondary"
          textDecoration="none"
        >
          <ExternalLinkIcon height={12} width={12} />
        </Flex>
      </Flex>
    </RedoTablePaddedCell>
  );
});

function getMerchantAdminPath(teamId: string): string {
  return `/teams/${teamId}/general`;
}

function productCodeLabel(productCode: string): string {
  switch (productCode) {
    case "chargebacks":
      return "Chargebacks";
    default:
      return productCode;
  }
}
